import React from 'react';
import { InspectorPanel } from 'components/wysiwyg/InspectorPanel/InspectorPanel';
import { TemplatesSidebar } from 'components/wysiwyg/TemplatesSidebar';
import { EditorToolbar } from 'components/wysiwyg/EditorToolbar';
import { PreviewArea } from 'components/wysiwyg/PreviewArea';
import { RenderRequest, Variant, VariantWithTemplate } from 'types/cx-editor';
import { createCxApiClient } from 'framework/http/cxEditorClient';
import { useGlobalConfig } from 'context/GlobalContext';
import CompareDialog from 'components/wysiwyg/CompareDialog';
import { toast } from 'utils/toast';
import { Box } from '@mui/material';
import { MOCK_TEMPLATES_RESPONSE } from 'lib/asset-templates';
import { resolveScenarioId } from 'utils/cx-editor';

const DEFAULT_VARIANT: Variant = { hierarchy: 'Project', locale: 'en_US', scenario: 'default' };

// Module-level in-flight tracking to prevent duplicate API calls across component instances and StrictMode re-renders
const inFlightTemplateMeta = new Set<string>(); // Track template meta requests in progress

// Mock clusters - will be replaced with API data in Phase 2
const MOCK_CLUSTERS_WYSIWYG = [
  { id: "cluster_001", name: "EE Treatment" },
  { id: "cluster_002", name: "AB Test - $ in Subject" },
  { id: "cluster_003", name: "AB Test - No $ in Subject" },
];

export const WysiwygPage: React.FC = () => {
  const { rc } = useGlobalConfig();
  const [templateKey, setTemplateKey] = React.useState<string>('');
  const [elementKey, setElementKey] = React.useState<string>('Full Template');
  const [variant, setVariant] = React.useState<Variant>(DEFAULT_VARIANT);
  const [values, setValues] = React.useState<Record<string, any>>({});
  const [renderedHtml, setRenderedHtml] = React.useState<string>(''); // Store rendered HTML for technical section ID lookup
  const [compareOpen, setCompareOpen] = React.useState(false);
  const [variantB, setVariantB] = React.useState<VariantWithTemplate | null>(null);
  const [availableScenarios, setAvailableScenarios] = React.useState<Array<{ value: string; label: string }>>([]);
  const [scenariosLoading, setScenariosLoading] = React.useState(false);
  const [availableElements, setAvailableElements] = React.useState<Array<{ elementKey: string; displayName: string }>>([
    { elementKey: 'Full Template', displayName: 'Full Template' }
  ]); // Elements parsed from rendered HTML
  const [sidebarOpen, setSidebarOpen] = React.useState(true);
  const [inspectorOpen, setInspectorOpen] = React.useState(true);
  const [previewMode, setPreviewMode] = React.useState<'desktop' | 'mobile'>('desktop');
  const inspectorSaveRef = React.useRef<(() => Promise<void>) | null>(null); // Store InspectorPanel save function
  const inspectorCancelRef = React.useRef<(() => void) | null>(null); // Store InspectorPanel cancel function
  const [modifiedCount, setModifiedCount] = React.useState<number>(0); // Track number of modified fields
  const [refreshKey, setRefreshKey] = React.useState<number>(0); // Refresh trigger to force preview re-render after save

  // Initialize variantA when templateKey or variant changes
  // Use useMemo to prevent unnecessary object recreation
  const variantA = React.useMemo(() => {
    if (!templateKey) return null;
    return {
      templateKey,
      ...variant,
    };
  }, [templateKey, variant.hierarchy, variant.locale, variant.scenario]);

  // Scenarios are now loaded by EditorToolbar and passed via callback to avoid duplicate API calls
  // This useEffect is kept as fallback but will be populated by EditorToolbar's onScenariosLoaded callback
  const handleScenariosLoaded = React.useCallback((scenarios: Array<{ label: string; value: string }>) => {
    console.log('[WysiwygPage] Scenarios received from EditorToolbar:', scenarios);
    setAvailableScenarios(scenarios);
  }, []);
  
  // Track if we should fetch template meta after render completes
  const [shouldFetchTemplateMeta, setShouldFetchTemplateMeta] = React.useState(false);
  const lastFetchedTemplateKeyRef = React.useRef<string>('');
  
  // Handle render completion - trigger template/scenario fetch
  const handleRenderComplete = React.useCallback((completedTemplateKey: string) => {
    console.log('[WysiwygPage] Render complete for template:', completedTemplateKey);
    // Only trigger if this is a new template (not already fetched)
    if (completedTemplateKey && completedTemplateKey !== lastFetchedTemplateKeyRef.current) {
      setShouldFetchTemplateMeta(true);
    }
  }, []);
  
  // Fetch template meta and scenarios in parallel AFTER render completes
  // This is centralized here to prevent duplicate calls from multiple EditorToolbar instances
  React.useEffect(() => {
    if (!shouldFetchTemplateMeta || !templateKey) {
      return;
    }
    
    const requestKey = `${templateKey}-${elementKey || ''}`;
    
    // Check if already in flight
    if (inFlightTemplateMeta.has(requestKey)) {
      console.log('[WysiwygPage] Template meta request already in flight, skipping duplicate:', requestKey);
      return;
    }
    
    // Mark as in-flight
    inFlightTemplateMeta.add(requestKey);
    lastFetchedTemplateKeyRef.current = templateKey;
    
    let ignore = false;
    async function load() {
      setScenariosLoading(true);
      try {
        const api = createCxApiClient(rc, process.env.REACT_APP_TOOL_API_URL as string);
        console.log('[WysiwygPage] Fetching template meta after render complete:', {
          templateKey,
          elementKey,
          requestKey
        });
        
        const meta = await api.getTemplateMeta(templateKey, elementKey);
        
        if (!ignore) {
          // Remove from in-flight
          inFlightTemplateMeta.delete(requestKey);
          
          // Update scenarios
          const apiScenarios = meta.scenarios || [];
          if (apiScenarios.length > 0) {
            setAvailableScenarios(apiScenarios);
            handleScenariosLoaded(apiScenarios);
          } else {
            const defaultScenarios = [{ label: 'Default', value: 'default' }];
            setAvailableScenarios(defaultScenarios);
            handleScenariosLoaded(defaultScenarios);
          }
          
          // Update elements if not already set from parsed HTML
          if (!availableElements || availableElements.length === 0) {
            setAvailableElements(meta.elements || []);
          }
          
          setShouldFetchTemplateMeta(false);
        }
      } catch (error: any) {
        if (!ignore) {
          console.error('[WysiwygPage] Failed to load template meta:', error);
          inFlightTemplateMeta.delete(requestKey);
          setAvailableScenarios([{ label: 'Default', value: 'default' }]);
          setShouldFetchTemplateMeta(false);
        }
      } finally {
        if (!ignore) {
          setScenariosLoading(false);
        }
      }
    }
    load();
    return () => { ignore = true; };
  }, [shouldFetchTemplateMeta, templateKey, elementKey, rc, availableElements, handleScenariosLoaded]);

  // Determine channel from templateKey for PreviewArea
  const channel = React.useMemo(() => {
    if (!templateKey) return 'Email';
    if (templateKey.startsWith('WEB_') || templateKey.startsWith('DASHBOARD.')) return 'Web';
    if (templateKey === 'HER') return 'HER';
    if (templateKey.startsWith('SMS.')) return 'Email'; // SMS maps to Email channel type
    return 'Email';
  }, [templateKey]);

  // Auto-collapse sidebar when Web channel is selected (for larger preview)
  // Only collapse when channel changes to Web, not when sidebarOpen changes
  const prevChannelRef = React.useRef<string>('');
  React.useEffect(() => {
    // Only auto-collapse if channel changed to Web (not if it was already Web)
    if (channel === 'Web' && prevChannelRef.current !== 'Web' && sidebarOpen) {
      setSidebarOpen(false);
    }
    prevChannelRef.current = channel;
  }, [channel, templateKey]); // Removed sidebarOpen from dependencies to allow manual expansion

  // Prepare available templates list
  const availableTemplates = React.useMemo(() => {
    return MOCK_TEMPLATES_RESPONSE.channels
      .flatMap(ch => ch.templates)
      .map(t => ({ templateKey: t.templateKey, name: t.name }));
  }, []);

  // Memoize renderReq to prevent unnecessary re-renders in PreviewArea
  // Include refreshKey to force re-render after save while preserving elementKey and scenario
  const renderReq: RenderRequest | null = React.useMemo(() => {
    return variantA
      ? { templateKey: variantA.templateKey, elementKey, variant: { hierarchy: variantA.hierarchy, locale: variantA.locale, scenario: variantA.scenario }, previewMode: 'desktop', values, refreshKey }
      : null;
  }, [variantA?.templateKey, variantA?.hierarchy, variantA?.locale, variantA?.scenario, elementKey, values, refreshKey]);
  
  const renderReqB: RenderRequest | null = React.useMemo(() => {
    return variantB
      ? { templateKey: variantB.templateKey, elementKey, variant: { hierarchy: variantB.hierarchy, locale: variantB.locale, scenario: variantB.scenario }, previewMode: 'desktop', values }
      : null;
  }, [variantB?.templateKey, variantB?.hierarchy, variantB?.locale, variantB?.scenario, elementKey, values]);

  const handleVariantChange = (variant: 'A' | 'B', data: VariantWithTemplate) => {
    if (variant === 'A') {
      // Update the main templateKey and variant - variantA will be recalculated via useMemo
      if (data.templateKey !== templateKey) {
        setTemplateKey(data.templateKey);
      }
      setVariant({ hierarchy: data.hierarchy, locale: data.locale, scenario: data.scenario });
    } else {
      setVariantB(data);
    }
  };

  const handleCompareRun = () => {
    // Both variants are already set via onChange, just close the dialog
    // The renderReq and renderReqB will automatically update
  };

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          height: 'calc(100vh - 8rem)',
          gap: 0,
          overflow: 'hidden'
        }}
      >
        <Box
          sx={{
            width: sidebarOpen ? 300 : 48,
            minWidth: sidebarOpen ? 300 : 48,
            transition: 'width 300ms ease-in-out',
            flexShrink: 0,
            overflow: 'hidden'
          }}
        >
          <TemplatesSidebar
            onSelect={setTemplateKey}
            selectedTemplateKey={templateKey}
            isOpen={sidebarOpen}
            onToggleOpen={() => setSidebarOpen(o => !o)}
          />
        </Box>

        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            minWidth: 0,
            pl: 2,
            gap: 2,
            overflow: 'hidden'
          }}
        >
          {/* EditorToolbar Upper Section - Full Width */}
          <Box sx={{ width: '100%' }}>
            <EditorToolbar
              templateKey={templateKey}
              variant={variant}
              channel={channel}
              previewMode={previewMode}
              onVariantChange={setVariant}
              elementKey={elementKey}
              onElementChange={setElementKey}
              onPreviewModeChange={setPreviewMode}
              onOpenCompare={() => setCompareOpen(true)}
              onCancel={() => {
                // Call InspectorPanel cancel function if available
                if (inspectorCancelRef.current) {
                  inspectorCancelRef.current();
                }
              }}
              onSave={async () => {
                // Call InspectorPanel save function if available
                if (inspectorSaveRef.current) {
                  await inspectorSaveRef.current();
                } else {
                  // Fallback to old save if InspectorPanel save not available
                  if (!templateKey) return;
                  try {
                    const api = createCxApiClient(rc, process.env.REACT_APP_TOOL_API_URL as string);
                    
                    // Resolve "default" scenario to actual scenario ID from data scenarios API
                    const resolvedScenario = await resolveScenarioId(
                      variant.scenario || 'default',
                      templateKey,
                      channel || 'Email',
                      () => api.getDataScenarios()
                    );
                    
                    const resp = await api.saveChanges({ 
                      templateKey, 
                      elementKey, 
                      variant: { ...variant, scenario: resolvedScenario }, 
                      changes: { text: [], colors: [], images: [], metadata: [] }, 
                      message: 'Quick save' 
                    });
                    toast.success(`Saved (version ${resp.versionId})`);
                  } catch (e: any) {
                    toast.error('Save failed');
                  }
                }
              }}
              renderOnlyUpperSection={true}
              availableElements={availableElements}
              availableScenarios={availableScenarios}
              scenariosLoading={scenariosLoading}
              onRenderComplete={handleRenderComplete}
              modifiedCount={modifiedCount}
            />
          </Box>

          {/* Flex Row: Left side (Lower Section + PreviewArea stacked), Right side (InspectorPanel) */}
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'row',
              gap: 2,
              minHeight: 0,
              overflow: 'hidden'
            }}
          >
            {/* Left side: Lower Section and PreviewArea stacked vertically */}
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                minWidth: 0,
                minHeight: 0,
                gap: 2,
                overflow: 'hidden'
              }}
            >
              {/* EditorToolbar Lower Section */}
              <Box sx={{ flexShrink: 0 }}>
                <EditorToolbar
                  templateKey={templateKey}
                  variant={variant}
                  channel={channel}
                  previewMode={previewMode}
                  onVariantChange={setVariant}
                  elementKey={elementKey}
                  onElementChange={setElementKey}
                  onPreviewModeChange={setPreviewMode}
                  onOpenCompare={() => setCompareOpen(true)}
                  onCancel={() => {
                    // Call InspectorPanel cancel function if available
                    if (inspectorCancelRef.current) {
                      inspectorCancelRef.current();
                    }
                  }}
                  onSave={async () => {
                    // Call InspectorPanel save function if available
                    if (inspectorSaveRef.current) {
                      await inspectorSaveRef.current();
                    } else {
                      // Fallback to old save if InspectorPanel save not available
                      if (!templateKey) return;
                      try {
                        const api = createCxApiClient(rc, process.env.REACT_APP_TOOL_API_URL as string);
                        const resp = await api.saveChanges({ templateKey, elementKey, variant, changes: { text: [], colors: [], images: [], metadata: [] }, message: 'Quick save' });
                        toast.success(`Saved (version ${resp.versionId})`);
                      } catch (e: any) {
                        toast.error('Save failed');
                      }
                    }
                  }}
                  renderOnlyLowerSection={true}
                  availableElements={availableElements}
                  availableScenarios={availableScenarios}
                  scenariosLoading={scenariosLoading}
                  onRenderComplete={handleRenderComplete}
                  modifiedCount={modifiedCount}
                />
              </Box>

              {/* PreviewArea */}
              <Box
                sx={{
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  minWidth: 0,
                  minHeight: 0,
                  overflow: 'hidden'
                }}
              >
                <PreviewArea 
                  request={renderReq} 
                  rightRequest={renderReqB}
                  previewMode={previewMode}
                  channel={channel}
                  isFullTemplateSelected={elementKey === 'Full Template'}
                  elementKey={elementKey}
                  onSectionsParsed={(sections) => {
                    // Update available elements from parsed HTML sections
                    setAvailableElements(sections);
                    // If current elementKey is not in the new list, reset to Full Template
                    if (!sections.find(s => s.elementKey === elementKey)) {
                      setElementKey('Full Template');
                    }
                  }}
                  onRenderComplete={handleRenderComplete}
                  onHtmlChange={(html) => {
                    setRenderedHtml(html);
                  }}
                />
              </Box>
            </Box>

            {/* Right side: InspectorPanel */}
            <Box
              sx={{
                width: inspectorOpen ? 400 : 48,
                minWidth: inspectorOpen ? 400 : 48,
                transition: 'width 300ms ease-in-out',
                flexShrink: 0,
                overflow: 'hidden'
              }}
            >
              <InspectorPanel
                templateKey={templateKey}
                elementKey={elementKey}
                variant={variant}
                onValuesChange={setValues}
                isOpen={inspectorOpen}
                onToggleOpen={() => setInspectorOpen(o => !o)}
                renderedHtml={renderedHtml}
                onSaveReady={(saveFn) => {
                  inspectorSaveRef.current = saveFn;
                }}
                onCancelReady={(cancelFn) => {
                  inspectorCancelRef.current = cancelFn;
                }}
                onModifiedCountChange={(count) => {
                  setModifiedCount(count);
                }}
                onSaveSuccess={() => {
                  // Increment refreshKey to trigger preview re-render after save
                  // This preserves the current elementKey and scenario
                  setRefreshKey(prev => prev + 1);
                }}
              />
            </Box>
          </Box>
        </Box>
      </Box>

      <CompareDialog
        open={compareOpen}
        onOpenChange={setCompareOpen}
        variantA={variantA}
        variantB={variantB}
        onChange={handleVariantChange}
        onRun={handleCompareRun}
        availableTemplates={availableTemplates}
        availableScenarios={availableScenarios}
        availableClusters={MOCK_CLUSTERS_WYSIWYG}
      />
    </>
  );
}

export default WysiwygPage;


